<template>
  <div class="confidentiality-step">
    <!-- Title -->
    <h2 class="step-title">Confidentiality Agreement</h2>
    <p class="step-description">
      Please read the following confidentiality agreement carefully. You must scroll through the entire document before you can accept and continue.
    </p>

    <!-- Optional: open PDF in a separate tab -->
    <div class="open-external">
      <button class="btn-open" @click="openInNewTab">
        <v-icon size="18">mdi-open-in-new</v-icon>&nbsp;Open PDF in new tab
      </button>
    </div>

    <!-- General error banner -->
    <div v-if="generalError" class="error-message general-error">{{ generalError }}</div>

    <!-- PDF Container -->
    <div
      ref="pdfContainer"
      class="pdf-container"
      @scroll="onScroll"
    >
      <!-- We intentionally set a large height on the embed so that the container becomes scrollable -->
      <embed
        :src="pdfSrc"
        type="application/pdf"
        width="100%"
        :height="pdfHeight"
      />
    </div>

    <!-- Agreement Checkbox -->
    <div class="agreement-checkbox">
      <p v-if="!hasScrolledToEnd" class="scroll-notice">
        <strong>You need to scroll until the last page for us to check that you have read the document.</strong>
      </p>
      <p class="user-info"><strong>First Name:</strong> {{ formData.firstName }}</p>
      <p class="user-info"><strong>Second Name:</strong> {{ formData.lastName }}</p>
      <div class="checkbox-wrapper">
        <input
          type="checkbox"
          id="acceptAgreement"
          v-model="accepted"
          :disabled="!hasScrolledToEnd"
        />
        <label for="acceptAgreement">I have read and accept the confidentiality agreement.</label>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons">
      <button class="btn btn-back" @click="handleBack" :disabled="isSubmitting">
        <v-icon>mdi-arrow-left</v-icon> Back
      </button>

      <button
        class="btn btn-continue"
        @click="handleContinue"
        :disabled="isSubmitting || !accepted"
      >
        <template v-if="isSubmitting">
          <v-progress-circular indeterminate color="white" size="20" class="spinner" />
        </template>
        <template v-else>
          Continue <v-icon>mdi-arrow-right</v-icon>
        </template>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RegisterConfidentiality',
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      pdfSrc: '/NDA.pdf',
      pdfHeight: '1200px', // Arbitrary height to ensure scrollability
      accepted: false,
      hasScrolledToEnd: false,
      isSubmitting: false,
      generalError: '',
    };
  },
  methods: {
    onScroll() {
      const el = this.$refs.pdfContainer;
      if (!el) return;
      // Allow a 10-pixel threshold from the very bottom
      const threshold = 10;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
        this.hasScrolledToEnd = true;
      }
    },
    openInNewTab() {
      // Use full path stored in pdfSrc so relative public file works in production build
      window.open(this.pdfSrc, '_blank');
    },
    handleBack() {
      this.$emit('prev-step');
    },
    handleContinue() {
      if (!this.accepted) {
        this.generalError = 'You must accept the agreement to continue.';
        return;
      }
      this.generalError = '';
      this.isSubmitting = true;

      // No backend communication is needed for this step.
      // Simply move to the next step after a short delay for UX consistency.
      setTimeout(() => {
        this.$emit('next-step');
        this.isSubmitting = false;
      }, 300);
    },
  },
};
</script>

<style scoped>
.confidentiality-step {
  background: white;
  border-radius: 8px;
  padding: 30px;
  max-width: 1200px; /* wider layout */
  margin: 0 auto;
  width: 100%;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  text-align: center;
}

.step-description {
  text-align: center;
  color: #666;
  margin-bottom: 25px;
}

.pdf-container {
  height: 700px; /* bigger viewer */
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 20px;
}

.open-external {
  display: flex;
  justify-content: center; /* center alignment */
  margin-bottom: 10px;
}

.btn-open {
  background-color: #fff;
  color: #4A6282;
  border: 1px solid #4A6282;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.btn-open:hover {
  background-color: #f1f5f9;
}

.agreement-checkbox {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 25px;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
}

.scroll-notice {
  margin: 0 0 10px 0;
  background-color: #fffbe5; /* light yellow */
  border: 1px solid #ffe58f; /* darker yellow border */
  color: #856404; /* dark yellow/brown text for accessibility */
  padding: 10px 15px;
  border-radius: 4px;
  font-weight: 600;
}

.user-info {
  margin: 0 0 6px 0;
  color: #666;
  font-size: 14px;
}

.agreement-checkbox input[type='checkbox'] {
  margin-right: 10px;
  width: 18px;
  height: 18px;
  accent-color: #4A6282; /* custom blue */
}

.action-buttons {
  display: flex;
  width: 100%;
}

.action-buttons .btn {
  flex: 1;
}

.action-buttons .btn + .btn {
  margin-left: 15px; /* keep spacing but ensure consistent layout */
}

.btn {
  /* Remove individual border-radius so they align nicely when adjacent */
  border-radius: 4px;
  padding: 12px 0;
  font-size: 16px;
  cursor: pointer;
  border: 1px solid transparent;
  text-align: center;
}

.btn-back {
  background-color: #fff;
  color: #333;
  border-color: #ddd;
}

.btn-back:hover {
  background-color: #f1f1f1;
}

.btn-continue {
  background-color: #4A6282;
  color: white;
}

.btn-continue:hover {
  background-color: #3d5270;
}

.btn-continue:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.general-error {
  text-align: center;
  color: #e94057;
  margin-bottom: 10px;
}

.spinner {
  margin: 0;
}
</style> 